import React, { useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ReceiptIcon,
  ArrowUpward as PaymentIcon,
  AutoAwesome as DraftIcon,
  Block as VoidIcon,
  Cancel as CancelledIcon,
  CheckCircle as ClearedIcon,
  DoneAll as CompletedIcon,
  ErrorOutline as UnmatchedIcon,
  Link as MatchedIcon,
  Lightbulb as ResolveIcon,
  MoreVert as MoreVertIcon,
  Schedule as PendingIcon,
  Send as PostedIcon,
  ThumbUp as ApprovedIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Shared Cash & Bank UI kit — same visual vocabulary as Journals.js and
// CoaAccounts.js so the ledger screens read as one product:
// word-labeled "⋮" row menus, outlined icon status chips, bordered stat
// strips, plus the envelope/format helpers every cash tab repeats.
// ---------------------------------------------------------------------------

export const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

export const unwrap = (response) => response?.data?.data ?? response?.data ?? {};

export const unwrapList = (response) => {
  const payload = unwrap(response);
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

export const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Row actions as a "⋮" dropdown with word labels — same pattern as
// Journals.js JournalRowActionsMenu and CoaAccounts.js RowActionsMenu: an
// icon-only row forces a hover just to find out what a button does, and
// doesn't scale once destructive + workflow actions share a row.
// items: [{ icon?, label, onClick, danger?, dividerBefore? }] | { divider: true }
// Renders nothing when there are no items.
export const RowActionsMenu = ({ items = [] }) => {
  const [anchor, setAnchor] = useState(null);
  const visible = (items || []).filter(Boolean);
  if (!visible.length) return null;
  const close = () => setAnchor(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ elevation: 2, sx: { minWidth: 190 } }}
      >
        {visible.map((it, i) => {
          if (it.divider) return <Divider key={`div-${i}`} />;
          const Icon = it.icon;
          return (
            <MenuItem
              key={it.label || `item-${i}`}
              onClick={() => { close(); it.onClick?.(); }}
              sx={it.danger ? { color: 'error.main' } : undefined}
            >
              {Icon && (
                <ListItemIcon>
                  {React.isValidElement(Icon) ? Icon : <Icon fontSize="small" color={it.danger ? 'error' : undefined} />}
                </ListItemIcon>
              )}
              {it.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

// Outlined icon status chip — same OAS treatment as Journals.js
// JournalStatusChip, extended to the cash-domain lifecycles
// (transfers, cheques, petty cash, vouchers, statements, match lines).
const STATUS_CFG = {
  DRAFT: { label: 'Draft', color: 'default', Icon: DraftIcon },
  APPROVED: { label: 'Approved', color: 'success', Icon: ApprovedIcon },
  POSTED: { label: 'Posted', color: 'info', Icon: PostedIcon },
  VOID: { label: 'Void', color: 'error', Icon: VoidIcon },
  ISSUED: { label: 'Issued', color: 'info', Icon: PendingIcon },
  CLEARED: { label: 'Cleared', color: 'success', Icon: ClearedIcon },
  BOUNCED: { label: 'Bounced', color: 'error', Icon: UnmatchedIcon },
  CANCELLED: { label: 'Cancelled', color: 'default', Icon: CancelledIcon },
  READY: { label: 'Ready', color: 'info', Icon: PendingIcon },
  COMPLETED: { label: 'Completed', color: 'success', Icon: CompletedIcon },
  UNMATCHED: { label: 'Unmatched', color: 'warning', Icon: UnmatchedIcon },
  MATCHED: { label: 'Matched', color: 'success', Icon: MatchedIcon },
  RESOLVED: { label: 'Resolved', color: 'info', Icon: ResolveIcon },
  RECEIPT: { label: 'Receipt', color: 'success', Icon: ReceiptIcon },
  PAYMENT: { label: 'Payment', color: 'warning', Icon: PaymentIcon },
};

export const CashStatusChip = ({ status }) => {
  const st = String(status || 'DRAFT').toUpperCase();
  const cfg = STATUS_CFG[st] || { label: status || 'Draft', color: 'default', Icon: DraftIcon };
  const { Icon } = cfg;
  return <Chip icon={<Icon fontSize="small" />} label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
};

// Bordered stat strip — same treatment as AccountBalances.js StatStrip:
// tabular numerals, dividers, wraps on small screens.
export const StatTile = ({ label, value, color }) => (
  <Box sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight={700} color={color} sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export const StatStrip = ({ tiles = [] }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    divider={<Divider orientation="vertical" flexItem />}
    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}
  >
    {tiles.map((t) => (
      <StatTile key={t.label} {...t} />
    ))}
  </Stack>
);
